// ============================================================================
// KLINISOL CRO - Training Viewer Page (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import LessonViewer from '../components/training/LessonViewer';
import LessonCardViewer from '../components/training/LessonCardViewer';
import QuizSection from '../components/training/QuizSection';
import ProgressSidebar from '../components/training/ProgressSidebar';
import ElectronicSignature from '../components/training/ElectronicSignature';
import TrainingCertificate from '../components/training/TrainingCertificate';
import { 
    ArrowLeft, 
    ChevronLeft, 
    ChevronRight,
    BookOpen,
    FileQuestion,
    Clock,
    Loader2,
    AlertCircle,
    PowerOff,
    HelpCircle,
    X,
    BarChart3,
    ClipboardCheck,
    FileText,
    Award,
    Shield
} from 'lucide-react';

const TrainingViewer = () => {
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    // State management
    const [moduleData, setModuleData] = useState(null);
    const [sections, setSections] = useState([]);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [trainingRecord, setTrainingRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [progressData, setProgressData] = useState(null);
    
    // Modal states
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);
    const [certificateData, setCertificateData] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    
    // Section states
    const [currentProgress, setCurrentProgress] = useState(null);
    const [sectionStartTime, setSectionStartTime] = useState(null);

    // Load module data on mount
    useEffect(() => {
        loadModuleData();
    }, [moduleId]);

    // Start section when index changes
    useEffect(() => {
        if (sections.length > 0 && currentSectionIndex < sections.length) {
            startSection(sections[currentSectionIndex]);
        }
    }, [currentSectionIndex, sections]);

    const loadModuleData = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get(`/training/module/${moduleId}`);
            
            if (response.data.success) {
                const data = response.data.data;
                
                setModuleData(data.module);
                setSections(data.sections);
                setTrainingRecord(data.training_record);
                
                const firstIncomplete = data.sections.findIndex(
                    s => s.user_progress?.status !== 'completed'
                );
                setCurrentSectionIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
                
            } else {
                setError('Failed to load training module');
            }
        } catch (err) {
            console.error('Error loading module:', err);
            setError(err.response?.data?.message || 'Failed to load training module');
        } finally {
            setLoading(false);
        }
    };

    const startSection = async (section) => {
        if (section.user_progress?.status === 'completed') {
            return;
        }

        try {
            setSectionStartTime(new Date());
            
            if (!section.user_progress || section.user_progress.status === 'not_started') {
                const response = await api.post('/training/progress/start', {
                    moduleId: parseInt(moduleId),
                    sectionId: section.id
                });

                if (response.data.success) {
                    const progressId = response.data.data.progress_id;
                    setCurrentProgress(response.data.data);
                    
                    setSections(prev => prev.map(s => 
                        s.id === section.id 
                            ? { 
                                ...s, 
                                user_progress: { 
                                    ...s.user_progress, 
                                    id: progressId,
                                    status: 'in_progress' 
                                } 
                            }
                            : s
                    ));
                }
            }
        } catch (err) {
            console.error('Error starting section:', err);
        }
    };

    const completeSection = async (sectionId) => {
        try {
            const timeSpent = sectionStartTime 
                ? Math.floor((new Date() - sectionStartTime) / 1000)
                : 0;

            const section = sections.find(s => s.id === sectionId);
            const progressId = section?.user_progress?.id || currentProgress?.progress_id;

            if (!progressId) {
                console.error('No progress ID found');
                return;
            }

            const response = await api.post('/training/progress/complete', {
                progressId: progressId,
                timeSpent: timeSpent,
                lastPosition: null
            });

            if (response.data.success) {
                setSections(prev => prev.map(s => 
                    s.id === sectionId 
                        ? { 
                            ...s, 
                            user_progress: { 
                                ...s.user_progress, 
                                status: 'completed',
                                completed_at: new Date().toISOString()
                            } 
                        }
                        : s
                ));

                const allComplete = sections.every((s, idx) => 
                    idx === currentSectionIndex || s.user_progress?.status === 'completed'
                );

                if (allComplete && currentSectionIndex === sections.length - 1) {
                    setTimeout(() => {
                        setShowSignatureModal(true);
                    }, 500);
                } else {
                    moveToNextSection();
                }
            }
        } catch (err) {
            console.error('Error completing section:', err);
            alert('Failed to save progress. Please try again.');
        }
    };

    const handleQuizComplete = async (passed) => {
        if (passed) {
            const currentSection = sections[currentSectionIndex];
            await completeSection(currentSection.id);
        }
    };

    const moveToNextSection = () => {
        if (currentSectionIndex < sections.length - 1) {
            setCurrentSectionIndex(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const moveToPreviousSection = () => {
        if (currentSectionIndex > 0) {
            setCurrentSectionIndex(prev => prev - 1);
            window.scrollTo(0, 0);
        }
    };

    const navigateToSection = (index) => {
        const section = sections[index];
        
        if (index > 0 && isSectionLocked(index)) {
            alert('Please complete previous sections first');
            return;
        }

        setCurrentSectionIndex(index);
        window.scrollTo(0, 0);
    };

    const isSectionLocked = (index) => {
        if (index === 0) return false;

        for (let i = 0; i < index; i++) {
            const prevSection = sections[i];
            if (prevSection.is_required && prevSection.user_progress?.status !== 'completed') {
                return true;
            }
        }
        return false;
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleSignatureSubmit = async (signatureData) => {
        try {
            // Check if module requires signature
            if (moduleData?.requires_signature === 1 || moduleData?.requires_signature === true) {
                // Use the signature endpoint (with blockchain)
                const response = await api.post('/training/signature', {
                    trainingRecordId: trainingRecord.id,
                    signatureText: signatureData.signatureText,
                    pin: signatureData.pin
                });

                if (response.data.success) {
                    setCertificateData(response.data.data);
                    setShowSignatureModal(false);
                    setShowCertificate(true);
                }
            } else {
                // Use the certificate generation endpoint (no signature required)
                const response = await api.post('/training/signature/generate-certificate', {
                    trainingRecordId: trainingRecord.id
                });

                if (response.data.success) {
                    setCertificateData(response.data.data);
                    setShowSignatureModal(false);
                    setShowCertificate(true);
                }
            }
        } catch (err) {
            console.error('Error submitting signature:', err);
            throw err;
        }
    };

    const handleCertificateClose = () => {
        setShowCertificate(false);
        navigate('/training-portal');
    };

    const handleExit = () => {
        const confirmed = window.confirm(
            'Are you sure you want to exit? Your progress has been saved.'
        );
        if (confirmed) {
            navigate('/training-portal');
        }
    };

    const getCompletionPercentage = () => {
        const completed = sections.filter(s => s.user_progress?.status === 'completed').length;
        return sections.length > 0 ? Math.round((completed / sections.length) * 100) : 0;
    };

    const renderCurrentSection = () => {
        if (!sections.length || currentSectionIndex >= sections.length) {
            return (
                <div className="text-center py-12 text-gray-500">
                    No section available
                </div>
            );
        }

        const section = sections[currentSectionIndex];
        const isCompleted = section.user_progress?.status === 'completed';

        switch (section.section_type) {
            case 'lesson':
                // Use modern card-based viewer for lessons
                return (
                    <LessonCardViewer
                        content={section.content}
                        contentFormat={section.content_format}
                        onComplete={() => completeSection(section.id)}
                        isCompleted={isCompleted}
                    />
                );

            case 'quiz':
                const quizData = typeof section.quiz_data === 'string'
                    ? JSON.parse(section.quiz_data)
                    : section.quiz_data;
                
                const passingCriteria = typeof section.passing_criteria === 'string'
                    ? JSON.parse(section.passing_criteria)
                    : section.passing_criteria;

                return (
                    <QuizSection
                        sectionId={section.id}
                        moduleId={parseInt(moduleId)}
                        quizData={quizData}
                        passingCriteria={passingCriteria}
                        onComplete={handleQuizComplete}
                        isCompleted={isCompleted}
                    />
                );

            default:
                return (
                    <div className="text-center py-12">
                        <p className="text-gray-500">Unknown section type: {section.section_type}</p>
                    </div>
                );
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff]">
                <Loader2 className="w-12 h-12 text-[#475569] animate-spin mb-4" />
                <p className="text-gray-600 text-lg">Loading training module...</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-white">
                <div className="max-w-xl mx-auto text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Training</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button 
                        onClick={() => navigate('/training-portal')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#475569] text-white font-semibold rounded-lg hover:bg-[#334155] transition-all shadow-sm"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Training Portal
                    </button>
                </div>
            </div>
        );
    }

    // No module data
    if (!moduleData) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-white">
                <div className="max-w-xl mx-auto text-center bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                    <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-700 mb-4">Training Module Not Found</h2>
                    <button 
                        onClick={() => navigate('/training-portal')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#475569] text-white font-semibold rounded-lg hover:bg-[#334155] transition-all shadow-sm"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Training Portal
                    </button>
                </div>
            </div>
        );
    }

    const currentSection = sections[currentSectionIndex];
    const completionPercentage = getCompletionPercentage();

    return (
        <div className="flex flex-col h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] overflow-hidden">
            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg z-50">
                <div className="max-w-[1600px] mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Left Section */}
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={handleExit}
                                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg transition-all"
                                title="Back to Training Portal"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span className="font-medium">Back</span>
                            </button>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold">
                                    {moduleData.module_name}
                                </h1>
                                <p className="text-sm opacity-90">{moduleData.module_code}</p>
                            </div>
                        </div>

                        {/* Right Section */}
                        <div className="flex items-center gap-4">
                            {/* Circular Progress */}
                            <div className="relative w-16 h-16">
                                <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                                    <circle
                                        cx="18"
                                        cy="18"
                                        r="15.9155"
                                        fill="none"
                                        stroke="rgba(255, 255, 255, 0.2)"
                                        strokeWidth="3"
                                    />
                                    <circle
                                        cx="18"
                                        cy="18"
                                        r="15.9155"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="3"
                                        strokeDasharray={`${completionPercentage}, 100`}
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                                    {completionPercentage}%
                                </div>
                            </div>
                            {/* Help Button */}
                            <button 
                                onClick={() => setShowHelp(true)}
                                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all flex items-center justify-center"
                                title="How Training Works"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>
                            <div className="text-sm font-medium">
                                {user?.firstName} {user?.lastName}
                            </div>
                            <button 
                                onClick={handleLogout}
                                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                                title="Logout"
                            >
                                <PowerOff className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex flex-1 overflow-hidden">
                {/* Progress Sidebar */}
                <ProgressSidebar
                    sections={sections}
                    currentIndex={currentSectionIndex}
                    onNavigate={navigateToSection}
                />

                {/* Content Area */}
                <main className="flex-1 overflow-y-auto bg-white">
                    <div className="max-w-5xl mx-auto p-6 md:p-8">
                        {/* Section Header */}
                        <div className="mb-8 pb-6 border-b-2 border-gray-200">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                                <span className="inline-block px-3 py-1 bg-gray-100 text-[#475569] rounded text-sm font-semibold">
                                    Section {currentSection?.section_order}
                                </span>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-sm font-semibold ${
                                    currentSection?.section_type === 'lesson'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                    {currentSection?.section_type === 'lesson' ? (
                                        <>
                                            <BookOpen className="w-4 h-4" />
                                            Lesson
                                        </>
                                    ) : (
                                        <>
                                            <FileQuestion className="w-4 h-4" />
                                            Quiz
                                        </>
                                    )}
                                </span>
                                {currentSection?.is_required && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm font-semibold">
                                        Required
                                    </span>
                                )}
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-3">
                                {currentSection?.title}
                            </h2>
                            <div className="flex items-center gap-2 text-gray-600">
                                <Clock className="w-5 h-5" />
                                <span>Estimated time: {currentSection?.duration_minutes} minutes</span>
                            </div>
                        </div>

                        {/* Section Content */}
                        <div className="mb-8">
                            {renderCurrentSection()}
                        </div>

                        {/* Navigation Footer */}
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t-2 border-gray-200">
                            <button
                                onClick={moveToPreviousSection}
                                disabled={currentSectionIndex === 0}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all w-full md:w-auto"
                            >
                                <ChevronLeft className="w-5 h-5" />
                                Previous
                            </button>

                            <div className="text-center font-medium text-gray-600">
                                Section {currentSectionIndex + 1} of {sections.length}
                            </div>

                            <button
                                onClick={moveToNextSection}
                                disabled={
                                    currentSectionIndex === sections.length - 1 ||
                                    currentSection?.user_progress?.status !== 'completed'
                                }
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-semibold rounded-lg hover:from-[#334155] hover:to-[#1e293b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg w-full md:w-auto"
                                title={
                                    currentSection?.user_progress?.status !== 'completed'
                                        ? 'Complete this section to continue'
                                        : ''
                                }
                            >
                                Next
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                        <div className="max-w-5xl mx-auto text-center">
                            <h3 className="text-xl font-bold mb-2">
                                KLINiSOL
                            </h3>
                            <p className="text-sm opacity-90">&copy; 2025 KLINiSOL CRO. All rights reserved.</p>
                        </div>
                    </footer>
                </main>
            </div>

            {/* Electronic Signature Modal */}
            {showSignatureModal && (
                <ElectronicSignature
                    onSubmit={handleSignatureSubmit}
                    onCancel={() => setShowSignatureModal(false)}
                    moduleName={moduleData.module_name}
                    userName={`${user?.firstName} ${user?.lastName}`}
                />
            )}

            {/* Training Certificate Modal */}
            {showCertificate && certificateData && (
                <TrainingCertificate
                    certificateData={certificateData}
                    onClose={handleCertificateClose}
                    moduleInfo={{
                        name: moduleData.module_name,
                        code: moduleData.module_code
                    }}
                    userInfo={{
                        name: `${user?.firstName} ${user?.lastName}`
                    }}
                />
            )}

            {/* Help Modal */}
            {showHelp && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-[#475569] to-[#E91E63] text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <HelpCircle className="w-8 h-8" />
                                    <h2 className="text-2xl font-bold">Training Compliance Guide</h2>
                                </div>
                                <button 
                                    onClick={() => setShowHelp(false)}
                                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-white/90 mt-2 text-sm">FDA 21 CFR Part 11 Compliant Training Platform</p>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Section 1: Timed Sections */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-[#475569]" />
                                    Section Completion Requirements
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Each section has a <strong>minimum required reading time</strong> based on content length</li>
                                    <li>• The "Continue" button activates only after the timer completes</li>
                                    <li>• Time spent is tracked and recorded in your training record</li>
                                    <li>• You may spend additional time reviewing content as needed</li>
                                </ul>
                            </div>

                            {/* Section 2: Sequential Progression */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-[#E91E63]" />
                                    Sequential Progression
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Sections must be completed <strong>in order</strong> - no skipping allowed</li>
                                    <li>• Previous sections are marked with a ✓ checkmark when complete</li>
                                    <li>• Locked sections display a 🔒 icon until prerequisites are met</li>
                                    <li>• This ensures comprehensive understanding before advancing</li>
                                </ul>
                            </div>

                            {/* Section 3: Knowledge Assessment */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <ClipboardCheck className="w-5 h-5 text-[#475569]" />
                                    Knowledge Assessment
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Each module concludes with a <strong>quiz assessment</strong></li>
                                    <li>• Minimum passing score: <strong>80%</strong></li>
                                    <li>• Maximum <strong>3 attempts</strong> per assessment</li>
                                    <li>• All attempts are logged with score, date, and time</li>
                                    <li>• Contact your training administrator if additional attempts are needed</li>
                                </ul>
                            </div>

                            {/* Section 4: Electronic Signature */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-[#E91E63]" />
                                    Electronic Signature & Certification
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Upon passing, you must provide an <strong>electronic signature</strong></li>
                                    <li>• Enter your <strong>full legal name</strong> exactly as registered</li>
                                    <li>• Enter your <strong>secure PIN</strong> for identity verification</li>
                                    <li>• This constitutes a legally binding signature per 21 CFR Part 11</li>
                                </ul>
                            </div>

                            {/* Section 5: Certificate Validity */}
                            <div className="border-l-4 border-[#475569] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Award className="w-5 h-5 text-[#475569]" />
                                    Certificate Validity
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Certificates are valid for <strong>24 months</strong> from completion date</li>
                                    <li>• Each certificate includes a <strong>QR code</strong> for instant verification</li>
                                    <li>• Certificates are <strong>blockchain-secured</strong> for tamper-proof authenticity</li>
                                    <li>• Recertification required before expiration</li>
                                </ul>
                            </div>

                            {/* Section 6: Audit Trail */}
                            <div className="border-l-4 border-[#E91E63] pl-4">
                                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-[#E91E63]" />
                                    Audit Trail
                                </h3>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• All activity is automatically logged: start time, completion time, quiz attempts, signatures</li>
                                    <li>• Records are immutable and available for regulatory inspection</li>
                                    <li>• Training history accessible via "My Certificates" button</li>
                                </ul>
                            </div>

                            {/* Footer Note */}
                            <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-600 border border-gray-200">
                                <p>For questions, contact your training administrator.</p>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-gray-200 flex justify-end">
                            <button 
                                onClick={() => setShowHelp(false)}
                                className="px-6 py-2 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white font-semibold rounded-lg hover:opacity-90 transition-all"
                            >
                                Got It
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingViewer;